import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { useShopPath } from '@/hooks/useShopPath';

/**
 * Resolve which single state the "Today at a glance" action card shows.
 * Priority (per the "Logic of Today at a glance" brief):
 *   1. Active orders (Pending or Confirmed) win.
 *   2. Otherwise show the combined in-store visibility — the number of
 *      products shown to local shoppers across ALL channels combined, i.e.
 *      visibleProducts × activeChannels (2,490 products on 5 channels → 12,450).
 * Pure so both branches are unit-testable.
 *
 * @param {object} args
 * @param {number} [args.ordersToAction]
 * @param {number} [args.ordersTotalValue]
 * @param {number|null} [args.visibleProducts]
 * @param {number} [args.activeChannels]
 * @returns {{ status: 'orders-active', count: number, totalValue: number }
 *   | { status: 'products-visible', visibleProducts: number|null, activeChannels: number, combinedVisibility: number|null }}
 */
export function resolveActionCardStatus({ ordersToAction = 0, ordersTotalValue = 0, visibleProducts = null, activeChannels = 0 }) {
	if (ordersToAction > 0) {
		return { status: 'orders-active', count: ordersToAction, totalValue: ordersTotalValue };
	}
	const combinedVisibility = visibleProducts != null ? visibleProducts * activeChannels : null;
	return { status: 'products-visible', visibleProducts, activeChannels, combinedVisibility };
}

/**
 * The third KPI in the "Today at a glance" row. Single-slot: shows the orders
 * the retailer needs to action, or — when there are none — how many products
 * are visible across how many active channels. Numbers come from real hooks
 * via props; no fabricated values.
 */
export function ActionCard({ ordersToAction = 0, ordersTotalValue = 0, visibleProducts = null, activeChannels = 0, currency = 'GBP' }) {
	const { shopPath } = useShopPath();
	const ctx = resolveActionCardStatus({ ordersToAction, ordersTotalValue, visibleProducts, activeChannels });

	if (ctx.status === 'orders-active') {
		const value = ctx.totalValue.toLocaleString('en-GB', { style: 'currency', currency });
		return (
			<SoftCard>
				<div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Orders to action</div>
				<div className="mt-3 flex items-baseline gap-3">
					<div className="text-4xl font-extrabold tracking-tight text-foreground md:text-5xl">{ctx.count}</div>
				</div>
				<div className="mt-2 text-sm text-muted-foreground">to action, total value {value}</div>
				<Link
					to={shopPath('/orders')}
					className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
				>
					Manage orders <ArrowRight className="h-3.5 w-3.5" />
				</Link>
			</SoftCard>
		);
	}

	return (
		<SoftCard>
			<div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">In-store visibility</div>
			<div className="mt-3 flex items-baseline gap-3">
				<div className="text-4xl font-extrabold tracking-tight text-foreground md:text-5xl">
					{ctx.combinedVisibility != null ? ctx.combinedVisibility.toLocaleString('en-GB') : '—'}
				</div>
			</div>
			<div className="mt-2 text-sm text-muted-foreground">
				{ctx.visibleProducts != null
					? `${ctx.visibleProducts.toLocaleString('en-GB')} products shown across ${ctx.activeChannels} active channel${ctx.activeChannels === 1 ? '' : 's'}`
					: `products shown across ${ctx.activeChannels} active channel${ctx.activeChannels === 1 ? '' : 's'}`}
			</div>
			<Link
				to={shopPath('/channels')}
				className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
			>
				View channels <ArrowRight className="h-3.5 w-3.5" />
			</Link>
		</SoftCard>
	);
}
